/**
 * Pure, framework-agnostic logic for the per-agent memory browser.
 *
 * It stays in the browser-shared layer so the React components and compiled
 * tests use the same helpers. No React, no fetch — only data shaping.
 *
 * Outbound wikilink neighbours are derived here because the memory browser API
 * returns the entity body but no resolved neighbour list. The browser parses
 * `[[…]]` links from the body client-side.
 */

import type {
  MemoryListTagsData,
  MemoryListTopicsData,
  MemoryRenameTopicData,
} from "../contracts/memory.js";
import { parseWikilinks } from "../contracts/memory.js";

export type { ParsedWikilink } from "../contracts/memory.js";
export { parseWikilinks };

export interface MemoryTopicFacet {
  id: string;
  label: string;
  count: number;
}

export interface MemoryTagFacet {
  name: string;
  count: number;
}

/**
 * Project the topic catalog into filter choices. The `entityCount` carried
 * by each catalog entry is authoritative (the Gateway index computes it), so
 * the catalogue surfaces it directly rather than re-counting a partial,
 * paginated entity page.
 */
export function buildMemoryTopicFacets(catalog: MemoryListTopicsData): MemoryTopicFacet[] {
  return catalog.topics.map((topic) => ({
    id: topic.id,
    label: topic.label,
    count: topic.entityCount,
  }));
}

export function buildMemoryTagFacets(catalog: MemoryListTagsData): MemoryTagFacet[] {
  return catalog.tags.map((tag) => ({
    name: tag.name,
    count: tag.entityCount,
  }));
}

/* --------------------------------------------------------------------- *
 * Wikilink neighbours (outbound, parsed client-side from the body)
 * --------------------------------------------------------------------- */

export interface OutboundNeighbours {
  entities: Array<{ handle: string; label?: string }>;
  topics: Array<{ id: string; label?: string }>;
  tags: Array<{ name: string; label?: string }>;
}

/**
 * Group the parsed wikilinks of one body into the outbound-neighbour buckets
 * the detail panel renders. Each target is de-duplicated (first occurrence
 * wins, so an explicit label is kept over a bare later mention).
 */
export function outboundNeighbours(body: string): OutboundNeighbours {
  const entities = new Map<string, { handle: string; label?: string }>();
  const topics = new Map<string, { id: string; label?: string }>();
  const tags = new Map<string, { name: string; label?: string }>();
  for (const link of parseWikilinks(body)) {
    if (link.kind === "entity" && !entities.has(link.handle)) {
      entities.set(link.handle, {
        handle: link.handle,
        ...(link.label ? { label: link.label } : {}),
      });
    } else if (link.kind === "topic" && !topics.has(link.id)) {
      topics.set(link.id, { id: link.id, ...(link.label ? { label: link.label } : {}) });
    } else if (link.kind === "tag" && !tags.has(link.name)) {
      tags.set(link.name, { name: link.name, ...(link.label ? { label: link.label } : {}) });
    }
  }
  return {
    entities: [...entities.values()],
    topics: [...topics.values()],
    tags: [...tags.values()],
  };
}

/* --------------------------------------------------------------------- *
 * Rename progress
 * --------------------------------------------------------------------- */

export interface RenameProgressSummary {
  /** True when the Gateway reported the rename succeeded with no failure. */
  ok: boolean;
  completedCount: number;
  pendingCount: number;
  /** Handles that did not rewrite — empty unless the Gateway reported one. */
  failedHandles: string[];
  failureReason?: string;
}

/**
 * Reduce the Gateway `{ok, completed, pending, failure}` rename payload to the
 * per-status counts and the failed-handle list the UI surfaces inline. The
 * wire shape carries at most one `failure`; the summary still exposes it as a
 * list so the renderer stays uniform if that ever widens.
 */
export function summarizeRenameProgress(progress: MemoryRenameTopicData): RenameProgressSummary {
  return {
    ok: progress.ok,
    completedCount: progress.completed.length,
    pendingCount: progress.pending.length,
    failedHandles: progress.failure ? [progress.failure.handle] : [],
    ...(progress.failure ? { failureReason: progress.failure.error } : {}),
  };
}

/* --------------------------------------------------------------------- *
 * Operator-action gating
 * --------------------------------------------------------------------- */

/**
 * The single predicate the browser uses to decide whether the operator-only
 * actions (rename topic/tag, delete entity/topic/tag) render at all. Kept as a
 * named function so the gate is one testable place rather than scattered
 * `viewerCanEdit &&` checks — a non-operator viewer must see NONE of these in
 * the DOM, not a disabled variant.
 */
export function canRunOperatorActions(viewerCanEdit: boolean | undefined): boolean {
  return viewerCanEdit === true;
}

/* --------------------------------------------------------------------- *
 * Entity sort orders
 * --------------------------------------------------------------------- */

export type EntitySortOrder = "mtime-desc" | "handle-asc";

export const ENTITY_SORT_ORDERS: ReadonlyArray<{ id: EntitySortOrder; label: string }> = [
  { id: "mtime-desc", label: "Recently updated" },
  { id: "handle-asc", label: "Name (A–Z)" },
];

export interface SortableEntity {
  handle: string;
  mtime: string;
}

/**
 * Order an already-fetched entity page. `mtime-desc` is the server's native
 * cursor order (the page already arrives sorted that way); `handle-asc` is a
 * client-side re-sort of the loaded rows. Sorting client-side only re-orders
 * what is loaded — it does not change the server cursor — so the control is
 * scoped to "sort what's on screen", which the UI states plainly.
 */
export function sortEntities<T extends SortableEntity>(
  entities: readonly T[],
  order: EntitySortOrder,
): T[] {
  const copy = [...entities];
  if (order === "handle-asc") {
    copy.sort((a, b) => a.handle.localeCompare(b.handle));
  } else {
    copy.sort((a, b) => b.mtime.localeCompare(a.mtime) || a.handle.localeCompare(b.handle));
  }
  return copy;
}
